import React from 'react';

import classNames from 'classnames';
import ChevronDownIcon from 'icons/chevron-down.svg';
import ChevronRightIcon from 'icons/chevron-right.svg';
import _ from 'lodash';
import PropTypes from 'prop-types';

export function SidebarMenu({ children }) {
  return (
    <div className='tw-flex tw-h-full tw-w-54 tw-flex-none tw-flex-col tw-border-0 tw-border-r-1 tw-border-solid tw-border-grey-100 tw-bg-white'>
      {children}
    </div>
  );
}

SidebarMenu.propTypes = {
  children: PropTypes.node.isRequired,
};

export function Accordion({ children, isOpen, triggerContent }) {
  return (
    <div className='tw-flex tw-flex-col tw-gap-1'>
      {triggerContent}
      {isOpen && <ul className='tw-mb-0 tw-list-none tw-pl-4'>{children}</ul>}
    </div>
  );
}

Accordion.propTypes = {
  children: PropTypes.node.isRequired,
  isOpen: PropTypes.bool,
  triggerContent: PropTypes.node.isRequired,
};

export function AccordionItem({ children, isActive, onClick }) {
  return (
    <li
      className={classNames(
        'tw-w-full tw-border-0 tw-border-l-2 tw-border-solid tw-py-0.5 tw-pl-1.5',
        isActive ? 'tw-border-blue-200' : 'tw-border-grey-100',
      )}
    >
      <SubsectionItem isActive={isActive} onClick={onClick}>
        {children}
      </SubsectionItem>
    </li>
  );
}

AccordionItem.propTypes = {
  children: PropTypes.node.isRequired,
  isActive: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

export function AccordionTrigger({ children, isActive, isOpen, onClick }) {
  const ChevronIcon = isOpen ? ChevronDownIcon : ChevronRightIcon;

  return (
    <button
      className={classNames(
        'tw-flex tw-items-center tw-justify-between tw-rounded-lg tw-border-0 tw-p-2 tw-text-left tw-text-grey-700 hover:tw-bg-blue-025',
        isActive ? 'tw-bg-blue-025' : 'tw-bg-white',
      )}
      onClick={onClick}
    >
      <div className='tw-mb-0 tw-flex tw-items-center tw-gap-2 tw-truncate tw-text-l tw-font-semibold'>
        {children}
      </div>
      <ChevronIcon className='tw-flex-none' height={20} width={20} />
    </button>
  );
}

AccordionTrigger.propTypes = {
  children: PropTypes.node.isRequired,
  isActive: PropTypes.bool,
  isOpen: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

export function Divider() {
  return <hr className='tw-m-0 tw-w-full tw-text-grey-100' />;
}

export function Item({ children, isActive, onClick }) {
  return (
    <button
      className={classNames(
        'tw-w-full tw-rounded-lg tw-border-0 tw-px-2 tw-py-2.5 tw-text-left',
        isActive ?
          'tw-bg-blue-025 tw-text-grey-900'
        : 'tw-bg-[inherit] tw-text-grey-700 hover:tw-bg-blue-025',
      )}
      onClick={onClick}
    >
      <div className='tw-mb-0 tw-flex tw-items-center tw-gap-1 tw-text-l tw-font-semibold'>
        {children}
      </div>
    </button>
  );
}

Item.propTypes = {
  children: PropTypes.node.isRequired,
  isActive: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

export function Subsection({ children, title }) {
  return (
    <div
      className='tw-flex tw-flex-col tw-gap-2 tw-pb-2 tw-pl-1 tw-pt-3'
      data-element-name={`sidebar-menu-${_.kebabCase(title)}-subsection`}
    >
      {!!title && (
        <p className='tw-mb-0 tw-pl-2 tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-400'>
          {title}
        </p>
      )}
      <div className='tw-flex tw-flex-col tw-gap-0.5 tw-pl-1'>{children}</div>
    </div>
  );
}

Subsection.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string,
};

export function SubsectionItem({ children, isActive, onClick }) {
  return (
    <button
      className={classNames(
        'tw-mb-0 tw-h-9 tw-w-full tw-truncate tw-rounded-lg tw-border-0 tw-px-3 tw-py-1.5 tw-text-left tw-text-m tw-font-medium',
        isActive ?
          'tw-bg-blue-025 tw-text-grey-900'
        : 'tw-bg-[inherit] tw-text-grey-700 hover:tw-bg-blue-025',
      )}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

SubsectionItem.propTypes = {
  children: PropTypes.node.isRequired,
  isActive: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};
